import { useMemo } from "react";
import type { WorkspaceSwitch } from "../../nav/types";
import { useConsultantShell } from "./ConsultantShellContext";

/**
 * The workspace switches a consultant sees in the user menu (docs/NAVIGATION.md
 * section 2): the consultancy's own-company portal (or its set-up dialog for a
 * firm admin) and the auditor portal when the firm owns an active bureau.
 */
export function useConsultantSwitches(): WorkspaceSwitch[] {
  const { selfOrg, permissions, user, enterOwnOrgPortal, openAuditorPortal, openSelfOrgSetup } = useConsultantShell();
  const firmAdmin = permissions.includes("firm:admin");
  const canActAsAuditor = user.canActAsAuditor;
  const selfOrgId = selfOrg?.id ?? null;
  const selfOrgName = selfOrg?.name ?? null;

  return useMemo<WorkspaceSwitch[]>(() => {
    const out: WorkspaceSwitch[] = [];
    if (selfOrgId) {
      out.push({
        id: "own-org",
        label: selfOrgName ? `${selfOrgName} portal` : "Your company's portal",
        description: "Manage your own compliance as a client",
        onSelect: () => { void enterOwnOrgPortal(); },
      });
    } else if (firmAdmin) {
      out.push({
        id: "setup-own-org",
        label: "Set up your company's portal",
        description: "Add your firm as a client of its own",
        onSelect: openSelfOrgSetup,
      });
    }
    if (canActAsAuditor) {
      out.push({
        id: "auditor",
        label: "Auditor portal",
        description: "Your bureau's audit workspace",
        onSelect: () => { void openAuditorPortal(); },
      });
    }
    return out;
  }, [selfOrgId, selfOrgName, firmAdmin, canActAsAuditor, enterOwnOrgPortal, openAuditorPortal, openSelfOrgSetup]);
}
